import { ListRecommendations } from "../../schema/icp";
import { OnboardingListTypeKey } from "../lists/onboardingListTypes";

export interface ListBullet {
	/** The list type this bullet previews — also its React key. */
	key: OnboardingListTypeKey;
	i18nKey: string;
	values: Record<string, string>;
}

/** Joins a recommended multi-value filter the way the bullet reads it out. */
const join = (values: string[]): string => values.join(", ");

/**
 * The concrete values each list type's preview sentence needs. An entry returns
 * `null` when the recommendation carries nothing concrete for that type — the
 * caller then falls back to the type's generic catalog description.
 */
const VALUES_BY_TYPE: Record<
	OnboardingListTypeKey,
	(recommendations: ListRecommendations) => Record<string, string> | null
> = {
	hiring: ({ hiringQuery }) => (hiringQuery ? { role: hiringQuery } : null),
	lookalike: ({ lookalikeDomains }) =>
		lookalikeDomains.length ? { domains: join(lookalikeDomains) } : null,
	leadDb: ({ jobTitles, industries }) =>
		jobTitles.length && industries.length
			? { titles: join(jobTitles), industries: join(industries) }
			: null,
	topic: ({ topic }) => (topic ? { topic } : null),
	newInRole: ({ jobTitles }) => (jobTitles.length ? { titles: join(jobTitles) } : null),
	gmaps: ({ businessType }) => (businessType ? { businessType } : null),
	aiCompany: ({ companyDescription }) =>
		companyDescription ? { description: companyDescription } : null,
	aiNews: ({ newsQuery }) => (newsQuery ? { news: newsQuery } : null),
	funding: ({ fundingRounds }) => (fundingRounds.length ? { rounds: join(fundingRounds) } : null),
	mna: ({ fundingCategories }) =>
		fundingCategories.length ? { sectors: join(fundingCategories) } : null,
};

/**
 * Turns the API's list recommendation into one bullet per recommended type —
 * the preview of exactly the lists the picker will offer in the next step.
 *
 * A type whose filter values came back blank (the API blanks the values that
 * drifted out of English) keeps its generic catalog description, so the bullet
 * still describes the right list instead of an empty sentence.
 */
export const recommendedListBullets = (recommendations: ListRecommendations): ListBullet[] =>
	recommendations.types.map((key) => {
		const values = VALUES_BY_TYPE[key](recommendations);

		return values
			? { key, i18nKey: `onboarding.chat.listPreview.${key}`, values }
			: { key, i18nKey: `onboarding.lists.types.${key}.description`, values: {} };
	});
